"use client"

import { useFormState, useFormStatus } from "react-dom"
import {
  logCustomerIn,
  resetPassword,
  setNewPassword,
} from "@modules/auth/actions"
import { Button, Input, Text } from "@medusajs/ui"
import { Inter } from "next/font/google"
import { useState } from "react"
import { redirect, useSearchParams } from "next/navigation"
import { Status } from "types/global"

const inter = Inter({ subsets: ["latin"] })

export enum AuthView {
  LOGIN = "login",
  FORGOT_PASSWORD = "forgotPassword",
  PASSWORD_RESET = "passwordReset",
}

const LoginBox = () => {
  const [loginInResponse, loginFormAction] = useFormState(
    logCustomerIn,
    undefined
  )

  if (loginInResponse?.status == Status.SUCCESS) {
    redirect("/cz/")
  }

  const [resetPasswordMessage, resetPasswordAction] = useFormState(
    resetPassword,
    null
  )
  const [setNewPasswordMessage, setNewPasswordAction] = useFormState(
    setNewPassword,
    null
  )

  const [view, setView] = useState<AuthView>(AuthView.LOGIN)

  const resetToken = useSearchParams().get("reset_token")

  if (view != AuthView.PASSWORD_RESET && resetToken != null) {
    setView(AuthView.PASSWORD_RESET)
  }

  return (
    <form
      className="flex flex-col items-center justify-center w-auto m-3 rounded-2xl bg-[#f4f4f4] h-auto gap-y-3 mt-40 pt-7 pl-14 pr-14 pb-7"
      action={
        view == AuthView.LOGIN
          ? loginFormAction
          : view == AuthView.FORGOT_PASSWORD
          ? resetPasswordAction
          : setNewPasswordAction
      }
      autoComplete={view == AuthView.PASSWORD_RESET ? "off" : undefined}
    >
      {/* Heading */}
      <h1 className={`${inter.className} font-medium text-2xl pb-3`}>
        {view == AuthView.LOGIN
          ? `Přihlášení zákazníka`
          : view == AuthView.FORGOT_PASSWORD
          ? `Zapomenuté heslo`
          : `Obnovení hesla`}
      </h1>

      {/* Login error message */}
      <div
        hidden={
          view == AuthView.LOGIN && loginInResponse?.status == Status.ERROR
            ? false
            : true
        }
        className="w-full p-2 mb-4 text-center text-red-600 border-2 border-red-600 font-medi m rounded-2xl"
      >
        <h1>{loginInResponse?.message}</h1>
      </div>

      {/* Password reset request message */}
      <div
        hidden={
          view == AuthView.FORGOT_PASSWORD && resetPasswordMessage != null
            ? false
            : true
        }
        className={`w-full p-2 mb-4 text-center border-2 ${
          resetPasswordMessage?.state == "error"
            ? "text-red-600  border-red-600"
            : "text-green-600 border-green-600"
        } font-medium rounded-2xl`}
      >
        <h1>{resetPasswordMessage?.message}</h1>
      </div>

      {/* Set new password */}
      <div
        hidden={
          view == AuthView.PASSWORD_RESET && setNewPasswordMessage != null
            ? false
            : true
        }
        className={`w-full p-2 mb-4 text-center border-2 ${
          setNewPasswordMessage?.state == "error"
            ? "text-red-600  border-red-600"
            : "text-green-600 border-green-600"
        } font-medium rounded-2xl`}
      >
        <h1>{setNewPasswordMessage?.message}</h1>
      </div>

      {/* E-mail Box */}
      {view == AuthView.LOGIN || view == AuthView.FORGOT_PASSWORD ? (
        <Input
          name="login-email"
          className="min-w-[25rem] w-full text-center mb-3 text-xl max-h-12 h-full"
          placeholder="E-mail"
          type="email"
          required={true}
        />
      ) : null}

      {/* Password Box */}
      {view == AuthView.LOGIN ? (
        <Input
          name="login-password"
          className="min-w-[25rem] w-full text-center pl-11 text-xl max-h-12 h-full"
          placeholder="•••••"
          type="password"
          required={true}
        />
      ) : null}

      {/* Set new Password boxes */}
      {view == AuthView.PASSWORD_RESET ? (
        <>
          <Text>Váš E-mail</Text>
          <Input
            name="new-password-email"
            className="min-w-[25rem] w-full text-center mb-3 text-xl max-h-12 h-full"
            placeholder="E-mail"
            type="email"
            autoComplete="new-password"
            required={true}
          />
          <Text>Nové heslo</Text>
          <Input
            name="new-password-first"
            className="min-w-[25rem] w-full text-center pl-11 text-xl max-h-12 h-full"
            placeholder="•••••"
            type="password"
            content=""
            autoComplete="new-password"
            required={true}
          />
          <Text>Opakujte nové heslo</Text>
          <Input
            name="new-password-second"
            className="min-w-[25rem] w-full text-center pl-11 text-xl max-h-12 h-full"
            placeholder="•••••"
            type="password"
            autoComplete="new-password"
            content=""
            required={true}
          />
        </>
      ) : null}

      {/* Send form Button */}
      <Button
        type="submit"
        name="button-submit"
        className="w-full rounded-lg min-w-[25rem] text-xl mt-3 p-2"
      >
        {view == AuthView.LOGIN ? "Přihlásit se" : "Obnovit heslo"}
      </Button>

      {/* Switch view link */}
      {view != AuthView.PASSWORD_RESET ? (
        <Text
          className="pt-2 text-base text-gray-600 hover:text-black hover:cursor-pointer hover:underline"
          onClick={() => {
            if (view == AuthView.FORGOT_PASSWORD) setView(AuthView.LOGIN)
            else setView(AuthView.FORGOT_PASSWORD)
          }}
        >
          {view == AuthView.LOGIN ? "Zapomenuté heslo" : "Zpět na přihlášení"}
        </Text>
      ) : null}
    </form>
  )
}

export default LoginBox
